import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { getCosts, getToken } from "../api/client.js";
import type { CostBreakdownItem, CostsResponse } from "../api/types.js";
import { useApi } from "../lib/useApi.js";
import { applyDemo } from "../lib/demo.js";
import { formatCurrency, formatPct } from "../lib/format.js";
import { Card, Chip, EmptyState, KpiCard, ProgressBar, SectionTitle, StatusDot } from "../components/ui.js";
import type { Tone } from "../components/ui.js";
import { StackedBars } from "../components/charts.js";
import { ErrorState, LoadingState, SectionErrors } from "../components/common.js";

interface CostsDailySeries {
  account: string;
  label: string;
  amounts: number[];
}

interface CostsDailyResponse {
  days: string[];
  series: CostsDailySeries[];
  generatedAt: string;
}

// /api/costs/daily is not in client.ts yet; probe it defensively and treat any failure as "not available".
async function getCostsDaily(): Promise<CostsDailyResponse | null> {
  const token = getToken();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  let res: Response;
  try {
    res = await fetch("/api/costs/daily", { headers });
  } catch {
    return null;
  }
  if (!res.ok) return null;
  let body: unknown;
  try {
    body = (await res.json()) as unknown;
  } catch {
    return null;
  }
  const raw = body as { days?: unknown; series?: unknown; generatedAt?: unknown } | null;
  if (!raw || !Array.isArray(raw.days) || !Array.isArray(raw.series)) return null;
  const days = (raw.days as unknown[]).filter((d): d is string => typeof d === "string");
  const series = (raw.series as unknown[]).filter((s): s is CostsDailySeries => {
    if (typeof s !== "object" || s === null) return false;
    const o = s as Record<string, unknown>;
    return typeof o.account === "string" && typeof o.label === "string" && Array.isArray(o.amounts);
  });
  if (days.length === 0 || series.length === 0) return null;
  return applyDemo("/api/costs/daily", {
    days,
    series,
    generatedAt: typeof raw.generatedAt === "string" ? raw.generatedAt : "",
  });
}

function optionalBreakdown(data: CostsResponse, keys: string[]): CostBreakdownItem[] | null {
  const rec = data as unknown as Record<string, unknown>;
  for (const key of keys) {
    const value = rec[key];
    if (Array.isArray(value) && value.length > 0) return value as CostBreakdownItem[];
  }
  return null;
}

function optionalNumber(data: CostsResponse, keys: string[]): number | null {
  const rec = data as unknown as Record<string, unknown>;
  for (const key of keys) {
    const value = rec[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return null;
}

// Mid-lightness muted hues that read on both light and dark surfaces; kept
// out of the core --acct-* set so non-core accounts never mimic prod/staging.
const EXTRA_ACCOUNT_COLORS = [
  "#8d7bc4",
  "#4dab9a",
  "#c99a4d",
  "#b87e9e",
  "#7a9e7e",
  "#8fa3bf",
];

// Known non-core org accounts get pinned colors so they stay stable
// regardless of their position in byAccount vs the daily series.
const PINNED_EXTRA_ACCOUNTS: [string, string][] = [
  ["daust", EXTRA_ACCOUNT_COLORS[0]],
  ["factorio", EXTRA_ACCOUNT_COLORS[1]],
  ["popay", EXTRA_ACCOUNT_COLORS[2]],
  ["sandbox", EXTRA_ACCOUNT_COLORS[3]],
];

function accountColor(label: string, index: number): string {
  const l = label.toLowerCase();
  if (l.includes("prod")) return "var(--acct-prod)";
  if (l.includes("stag")) return "var(--acct-staging)";
  if (l.includes("shared")) return "var(--acct-shared)";
  if (l.includes("manage") || l.includes("payer") || l.includes("dev")) return "var(--acct-dev)";
  for (const [needle, color] of PINNED_EXTRA_ACCOUNTS) {
    if (l.includes(needle)) return color;
  }
  return EXTRA_ACCOUNT_COLORS[index % EXTRA_ACCOUNT_COLORS.length];
}

function deltaTone(pct: number): Tone {
  if (pct > 15) return "alarm";
  if (pct > 3) return "warn";
  if (pct < -1) return "ok";
  return "nodata";
}

function moneyCompact(value: number): string {
  if (value >= 1000) return `$${(value / 1000).toFixed(value >= 10000 ? 1 : 2)}k`;
  return `$${value.toFixed(0)}`;
}

// Chart tick/tooltip variant: one decimal below $100 so small daily slices
// stay distinguishable ($12.4), k-notation above $1k ($1.2k).
function moneyTick(value: number): string {
  if (value === 0) return "$0";
  if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(1)}k`;
  return `$${value.toFixed(Math.abs(value) >= 100 ? 0 : 1)}`;
}

function refDate(iso: string): Date {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

function monthShort(d: Date): string {
  return d.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
}

function shortDay(iso: string): string {
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

interface MonthContext {
  day: number;
  monthName: string;
  prevMonthName: string;
  prevDay: number;
  prevDayFraction: number;
}

function monthContext(data: CostsResponse): MonthContext {
  const now = refDate(data.generatedAt);
  const day = now.getUTCDate();
  const prev = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  const daysInPrev = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0)).getUTCDate();
  return {
    day,
    monthName: monthShort(now),
    prevMonthName: monthShort(prev),
    prevDay: Math.min(day, daysInPrev),
    prevDayFraction: Math.min(1, day / daysInPrev),
  };
}

const DELTA_PILL_STYLE = { fontSize: 11, fontWeight: 600, padding: "2px 6px" } as const;

function DeltaPill({ pct }: { pct: number }) {
  return (
    <span className={`pill pill--${deltaTone(pct)} mono`} style={DELTA_PILL_STYLE}>
      {formatPct(pct)}
    </span>
  );
}

function KpiRow({ data }: { data: CostsResponse }) {
  const ctx = monthContext(data);
  const samePeriodActual = optionalNumber(data, [
    "grossSamePeriodPreviousMonth",
    "samePeriodPreviousMonth",
    "previousMonthSamePeriod",
  ]);
  const samePeriod = samePeriodActual ?? data.grossPreviousMonth * ctx.prevDayFraction;
  // Forecast is gross-scale (CE forecasts usage) — compare against gross prev month,
  // not the credit-netted previousMonthTotal (~$0 here), which explodes the percent.
  const forecastPct =
    data.forecastMonthEnd !== null && data.grossPreviousMonth > 0
      ? (data.forecastMonthEnd / data.grossPreviousMonth - 1) * 100
      : null;
  return (
    <div className="grid-kpi">
      <KpiCard
        label="MTD — all accounts"
        value={formatCurrency(data.grossMonthToDate, data.currency)}
        footnote={`${ctx.monthName} 1 – ${ctx.monthName} ${ctx.day} · gross unblended · net ${formatCurrency(data.monthToDateTotal, data.currency)}`}
      />
      <KpiCard
        label={`Same period — ${ctx.prevMonthName}`}
        value={formatCurrency(samePeriod, data.currency)}
        footnote={
          samePeriodActual !== null
            ? `${ctx.prevMonthName} 1 – ${ctx.prevMonthName} ${ctx.prevDay} actual`
            : `${ctx.prevMonthName} 1 – ${ctx.prevMonthName} ${ctx.prevDay} · pro-rated from closed month`
        }
      />
      <KpiCard
        label="Forecast — month end"
        value={data.forecastMonthEnd === null ? "n/a" : moneyCompact(data.forecastMonthEnd)}
        badge={forecastPct === null ? undefined : formatPct(forecastPct)}
        badgeTone={forecastPct === null ? undefined : deltaTone(forecastPct)}
        footnote={`vs ${ctx.prevMonthName} actual · run-rate basis`}
      />
      <KpiCard
        label={`${ctx.prevMonthName} actual`}
        value={formatCurrency(data.grossPreviousMonth, data.currency)}
        footnote="Full month · closed"
      />
    </div>
  );
}

function AccountChips({
  data,
  daily,
  selectedKey,
  onSelect,
}: {
  data: CostsResponse;
  daily: CostsDailyResponse | null;
  selectedKey: string;
  onSelect: (key: string) => void;
}) {
  const through =
    daily && daily.days.length > 0 ? daily.days[daily.days.length - 1] : data.generatedAt;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <Chip active={selectedKey === "all"} onClick={() => onSelect("all")}>
        All accounts
      </Chip>
      {data.byAccount.map((a, i) => (
        <Chip
          key={a.key}
          active={selectedKey === a.key}
          onClick={() => onSelect(a.key)}
          dot={accountColor(a.label, i)}
        >
          {a.label}
        </Chip>
      ))}
      <span style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--fg-faint)" }}>
        Cost Explorer · gross unblended · through {shortDay(through)}
      </span>
    </div>
  );
}

interface DailyBarSeries {
  label: string;
  color: string;
  values: number[];
}

function buildDailySeries(
  daily: CostsDailyResponse | null,
  selected: CostBreakdownItem | null,
  accounts: CostBreakdownItem[],
): DailyBarSeries[] {
  if (!daily) return [];
  const colors = new Map<string, string>();
  accounts.forEach((a, i) => {
    colors.set(a.key, accountColor(a.label, i));
    colors.set(a.label, accountColor(a.label, i));
  });
  return daily.series
    .filter((s) => !selected || s.account === selected.key || s.label === selected.label)
    .map((s, i) => ({
      label: s.label,
      color: colors.get(s.account) ?? colors.get(s.label) ?? accountColor(s.label, i),
      values: daily.days.map((_, di) => s.amounts[di] ?? 0),
      total: s.amounts.reduce((sum, v) => sum + v, 0),
    }))
    .sort((a, b) => b.total - a.total)
    .map(({ label, color, values }) => ({ label, color, values }));
}

function DailyChartCard({
  daily,
  loading,
  selected,
  accounts,
}: {
  daily: CostsDailyResponse | null;
  loading: boolean;
  selected: CostBreakdownItem | null;
  accounts: CostBreakdownItem[];
}) {
  const series = buildDailySeries(daily, selected, accounts);
  const days = daily?.days ?? [];
  const xLabels =
    days.length >= 3
      ? [shortDay(days[0]), shortDay(days[Math.floor((days.length - 1) / 2)]), shortDay(days[days.length - 1])]
      : days.map(shortDay);
  return (
    <Card style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <SectionTitle
        title={selected ? `Daily spend — ${selected.label}` : "Daily spend by account"}
        subtitle={
          days.length > 0
            ? `${shortDay(days[0])} – ${shortDay(days[days.length - 1])} · daily unblended`
            : "Daily unblended spend"
        }
        actions={
          series.length > 0
            ? series.map((s) => (
                <span
                  key={s.label}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 11.5,
                    color: "var(--fg-subtle)",
                  }}
                >
                  <StatusDot square color={s.color} />
                  {s.label}
                </span>
              ))
            : undefined
        }
      />
      {series.length > 0 ? (
        <StackedBars
          series={series}
          labels={xLabels}
          height={170}
          hover
          showYAxis
          formatValue={moneyTick}
          slotLabelFor={(i) => shortDay(days[i] ?? "")}
        />
      ) : loading ? (
        <LoadingState label="Loading daily spend" />
      ) : (
        <EmptyState
          title="No daily breakdown yet"
          message="The server does not expose /api/costs/daily; the stacked daily chart appears once it does."
          tone="nodata"
        />
      )}
    </Card>
  );
}

function accountMom(
  item: CostBreakdownItem,
  prevList: CostBreakdownItem[] | null,
  dayFraction: number,
): number | null {
  if (!prevList) return null;
  const prev = prevList.find((p) => p.key === item.key || p.label === item.label);
  if (!prev || prev.amount <= 0) return null;
  const paced = prev.amount * dayFraction;
  if (paced <= 0) return null;
  return (item.amount / paced - 1) * 100;
}

function ByAccountCard({
  data,
  selected,
}: {
  data: CostsResponse;
  selected: CostBreakdownItem | null;
}) {
  const ctx = monthContext(data);
  const prevByAccount = optionalBreakdown(data, [
    "byAccountPreviousMonth",
    "byAccountPrevious",
    "previousByAccount",
  ]);
  const rows = selected ? [selected] : data.byAccount;
  const max = Math.max(...rows.map((r) => r.amount), 0) || 1;
  const totalAll = data.byAccount.reduce((sum, a) => sum + a.amount, 0);
  const note =
    (data.momDeltaPct === null
      ? `Net MTD ${formatCurrency(data.monthToDateTotal, data.currency)}.`
      : `Net MTD ${formatCurrency(data.monthToDateTotal, data.currency)} vs ${ctx.prevMonthName} full month ${formatCurrency(data.previousMonthTotal, data.currency)} (${formatPct(data.momDeltaPct)}).`) +
    (prevByAccount
      ? ` Pills pace each account against the elapsed share of ${ctx.prevMonthName}.`
      : ` Per-account MoM appears once the API exposes previous-month account data.`);
  return (
    <Card style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <span className="display" style={{ fontSize: 14.5 }}>
        By account — MTD
      </span>
      {rows.length === 0 ? (
        <EmptyState
          title="No account breakdown"
          message="Cost Explorer returned no linked-account data for this month."
          tone="nodata"
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {rows.map((a) => {
            const idx = data.byAccount.findIndex((x) => x.key === a.key);
            const color = accountColor(a.label, idx < 0 ? 0 : idx);
            const mom = accountMom(a, prevByAccount, ctx.prevDayFraction);
            const share = totalAll > 0 ? (a.amount / totalAll) * 100 : 0;
            return (
              <div
                key={a.key}
                title={`${a.label}: ${formatCurrency(a.amount, data.currency)} · ${share.toFixed(1)}% of MTD`}
                style={{ display: "flex", flexDirection: "column", gap: 5 }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <StatusDot square color={color} />
                  <span
                    style={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: "var(--fg)",
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {a.label}
                  </span>
                  {mom !== null && <DeltaPill pct={mom} />}
                  <span className="mono" style={{ fontSize: 12.5, color: "var(--fg)" }}>
                    {formatCurrency(a.amount, data.currency)}
                  </span>
                </div>
                <ProgressBar pct={(a.amount / max) * 100} color={color} />
              </div>
            );
          })}
        </div>
      )}
      <div
        style={{
          marginTop: "auto",
          borderTop: "1px solid var(--divider)",
          paddingTop: 10,
          fontSize: 11.5,
          color: "var(--fg-subtle)",
          lineHeight: 1.5,
        }}
      >
        {note}
      </div>
    </Card>
  );
}

function ServiceRow({
  svc,
  currency,
  grid,
  hasPrev,
  prevItem,
  mtdShare,
  prevShare,
}: {
  svc: CostBreakdownItem;
  currency: string;
  grid: string;
  hasPrev: boolean;
  prevItem: CostBreakdownItem | null;
  mtdShare: number;
  prevShare: number;
}) {
  const delta = prevShare > 0 ? (mtdShare / prevShare - 1) * 100 : null;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: grid,
        gap: "0 12px",
        alignItems: "center",
        padding: "9px 20px",
        borderBottom: "1px solid var(--divider)",
      }}
    >
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "var(--fg)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {svc.label}
      </span>
      <span className="mono" style={{ fontSize: 12.5, color: "var(--fg)", textAlign: "right" }}>
        {formatCurrency(svc.amount, currency)}
      </span>
      {hasPrev && (
        <>
          <span className="mono" style={{ fontSize: 12.5, color: "var(--fg-subtle)", textAlign: "right" }}>
            {prevItem ? formatCurrency(prevItem.amount, currency) : "—"}
          </span>
          <span style={{ textAlign: "right" }}>
            {delta === null ? (
              <span className="pill pill--info" style={DELTA_PILL_STYLE}>
                new
              </span>
            ) : (
              <DeltaPill pct={delta} />
            )}
          </span>
        </>
      )}
    </div>
  );
}

function ServiceTable({
  data,
  expanded,
  onToggle,
}: {
  data: CostsResponse;
  expanded: boolean;
  onToggle: () => void;
}) {
  const ctx = monthContext(data);
  const prev = optionalBreakdown(data, [
    "byServicePreviousMonth",
    "byServicePrevious",
    "previousByService",
  ]);
  const services = data.byService;
  const mtdTotal = services.reduce((sum, s) => sum + s.amount, 0);
  const prevTotal = prev ? prev.reduce((sum, s) => sum + s.amount, 0) : 0;
  const hasPrev = prev !== null && prevTotal > 0;
  const grid = hasPrev ? "minmax(0, 2.4fr) 120px 130px 96px" : "minmax(0, 2.4fr) 130px";
  const visible = expanded ? services : services.slice(0, 10);

  if (services.length === 0) {
    return (
      <Card style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <SectionTitle title="Costs by service" subtitle="Gross usage month-to-date" />
        <EmptyState
          title="No service breakdown"
          message="Cost Explorer returned no service-level data for this month."
          tone="nodata"
        />
      </Card>
    );
  }

  return (
    <Card flush>
      <div style={{ padding: "14px 20px 10px" }}>
        <SectionTitle
          title="Costs by service"
          subtitle={
            hasPrev
              ? `Δ share compares each service's slice of MTD spend with its slice of ${ctx.prevMonthName} (full month) · gross unblended`
              : "Gross usage month-to-date · comparison and trend columns appear when the API exposes service history"
          }
        />
      </div>
      <div style={{ overflowX: "auto" }}>
        <div style={{ minWidth: hasPrev ? 700 : 420 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: grid,
              gap: "0 12px",
              padding: "7px 20px",
              borderTop: "1px solid var(--divider)",
              borderBottom: "1px solid var(--divider)",
              background: "var(--bg-subtle)",
            }}
          >
            <span className="label--table">Service</span>
            <span className="label--table" style={{ textAlign: "right" }}>
              MTD
            </span>
            {hasPrev && (
              <>
                <span className="label--table" style={{ textAlign: "right" }}>
                  {ctx.prevMonthName} total
                </span>
                <span className="label--table" style={{ textAlign: "right" }}>
                  Δ share
                </span>
              </>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {visible.map((svc) => {
              const prevItem = prev?.find((p) => p.key === svc.key || p.label === svc.label) ?? null;
              return (
                <ServiceRow
                  key={svc.key}
                  svc={svc}
                  currency={data.currency}
                  grid={grid}
                  hasPrev={hasPrev}
                  prevItem={prevItem}
                  mtdShare={mtdTotal > 0 ? svc.amount / mtdTotal : 0}
                  prevShare={prevItem && prevTotal > 0 ? prevItem.amount / prevTotal : 0}
                />
              );
            })}
          </div>
        </div>
      </div>
      {services.length > 10 && (
        <button
          type="button"
          onClick={onToggle}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            width: "100%",
            justifyContent: "center",
            padding: 11,
            border: "none",
            background: "transparent",
            color: "var(--accent)",
            fontFamily: "var(--font-body)",
            fontSize: 12.5,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {expanded ? "Show top 10" : `Show all ${services.length} rows`}
          {expanded ? (
            <ChevronUp size={14} aria-hidden="true" />
          ) : (
            <ChevronDown size={14} aria-hidden="true" />
          )}
        </button>
      )}
    </Card>
  );
}

export interface CostsPageProps {
  refreshKey: number;
}

export function CostsPage({ refreshKey }: CostsPageProps) {
  const costs = useApi<CostsResponse>(getCosts, [], refreshKey);
  const daily = useApi<CostsDailyResponse | null>(getCostsDaily, [], refreshKey);
  const [account, setAccount] = useState("all");
  const [showAllServices, setShowAllServices] = useState(false);

  if (costs.loading && !costs.data) {
    return (
      <Card>
        <LoadingState label="Loading costs" />
      </Card>
    );
  }
  if (costs.error && !costs.data) {
    return (
      <Card>
        <ErrorState message={costs.error} />
      </Card>
    );
  }
  if (!costs.data) {
    return (
      <Card>
        <EmptyState
          title="No cost data"
          message="Cost Explorer returned nothing for this billing account."
          tone="nodata"
        />
      </Card>
    );
  }

  const data = costs.data;
  const selected = data.byAccount.find((a) => a.key === account) ?? null;

  return (
    <>
      <KpiRow data={data} />
      <AccountChips
        data={data}
        daily={daily.data}
        selectedKey={selected?.key ?? "all"}
        onSelect={setAccount}
      />
      <div className="grid-split">
        <DailyChartCard
          daily={daily.data}
          loading={daily.loading}
          selected={selected}
          accounts={data.byAccount}
        />
        <ByAccountCard data={data} selected={selected} />
      </div>
      <ServiceTable
        data={data}
        expanded={showAllServices}
        onToggle={() => setShowAllServices((v) => !v)}
      />
      <SectionErrors errors={data.errors} />
    </>
  );
}
